/** アプリ本体: ドロップ → 解析 → グリッド / 行ビュー描画。すべてブラウザ内で完結する。 */
import { buildVfs, filesFromDataTransfer, clearStaging } from './vfs.js';
import { loadDataset, getCompressors } from './dataset.js';
import { Engine } from './engine.js';
import { poster, clearPosters, SKIPPED } from './thumbs.js';
import { sparkline, chart, rangeOf, colorsFor } from './chart.js';
import { emptyFilter, facetsOf, orderOf, isActive, describeFilter, NO_TASK } from './filter.js';

const $ = s => document.querySelector(s);
const $$ = s => [...document.querySelectorAll(s)];
const pad = (n, w = 3) => String(n).padStart(w, '0');
const fmtBytes = n => n > 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n > 1e6 ? `${(n / 1e6).toFixed(0)} MB` : `${(n / 1e3).toFixed(0)} KB`;

/** タスク種別の色。関節の色とは役割が違うので別パレットにする */
const TASK_COLORS =
  ['#4cc9f0', '#ffd166', '#06d6a0', '#f72585', '#b892ff', '#ff8c42',
   '#7dd3fc', '#a3e635', '#e879f9', '#fb7185', '#34d399', '#facc15'];
const taskColor = k => k < 0 ? '#5b6673' : TASK_COLORS[k % TASK_COLORS.length];

/**
 * CSP の style-src 'self' は inline style 属性も止めるので、
 * innerHTML では色を書かずに data 属性で運び、挿入後に CSSOM で塗る。
 */
function paintDots(root){
  for(const el of root.querySelectorAll('[data-tc]')) el.style.background = taskColor(+el.dataset.tc);
}

const S = {
  vfs: null, ds: null, view: 'grid', cam: null, rate: 1, sort: 'i',
  order: [], cards: new Map(), eng: new Engine(12), ranges: null,
  filter: emptyFilter(), facets: null, missing: new Map(),
  signal: 'all', jointColors: [],
  // attaching: <video> を用意している最中のカード。上限の数え漏れを防ぐ（refresh を参照）。
  // 番号ではなくカードそのものを入れる。描き直すと同じ番号の別カードができるため。
  attaching: new Set(), scrolling: false,
};

/** いま描く関節の番号。系列（pos / vel / torque）が分かれていれば選択中のものだけ */
function shownJoints(){
  if(S.signal === 'all') return null;                 // null = 全部
  return new Set(S.ds.jointChannel.flatMap((c, j) => c === S.signal ? [j] : []));
}

/* ---------------- 取り込み ---------------- */

function log(msg, kind = ''){
  const el = document.createElement('div');
  el.className = `logline ${kind}`;
  el.textContent = msg;
  $('#log').append(el);
  $('#log').scrollTop = $('#log').scrollHeight;
}

/** ドロップ画面にエラーを出す（無反応に見えるのを防ぐ） */
function dropFailed(msg){
  $('#dropError').textContent = msg;
  $('#dropError').hidden = false;
  log(msg, 'err');
  if($('#drop').hidden) $('#logbox').open = true;    // ビューア表示中は #dropError が見えないため
}

async function ingest(inputs){
  if(!inputs.length) return;
  reset();
  $('#drop').hidden = true;
  $('#loading').hidden = false;
  $('#log').innerHTML = '';
  log(`${inputs.length} 個の入力を受け取りました（${fmtBytes(inputs.reduce((a, i) => a + i.file.size, 0))}）`);

  try {
    const t0 = performance.now();
    S.vfs = await buildVfs(inputs, p => {
      $('#loadDetail').textContent = p.detail || '';
      if(p.phase === 'extract' && p.count) $('#loadSub').textContent = `${p.count} ファイル展開済み`;
    });
    for(const n of S.vfs.notes) log(n);
    log(`${S.vfs.map.size} ファイルを索引化（${fmtBytes(S.vfs.totalSize)}）`);

    $('#loadDetail').textContent = 'メタデータを解析中…';
    S.ds = await loadDataset(S.vfs, p => { $('#loadDetail').textContent = p.detail || ''; });
    S.ranges = S.ds.range;
    S.cam = S.ds.cams[0];
    S.jointColors = colorsFor(S.ds.joints.length);
    // pos / vel / torque は物理量が別物なので、既定では先頭（pos）だけを描く
    S.signal = S.ds.channels[0] ?? 'all';
    log(`${S.ds.version} / ${S.ds.episodes.length} エピソード / ${S.ds.cams.length} カメラ を ${Math.round(performance.now() - t0)} ms で解析`, 'ok');

    S.missing = new Map();
    for(const e of S.ds.episodes){
      const gone = S.ds.cams.filter(c => !S.vfs.has(e.cams[c]?.path));
      if(gone.length) S.missing.set(e.i, gone);
    }
    if(S.missing.size){
      const [i, gone] = [...S.missing][0];
      log(`動画が欠けているエピソードが ${S.missing.size} 件あります（例: #${i} ${gone.join(', ')}）`, 'warn');
    }
    S.facets = facetsOf(S.ds, S.missing);
    S.filter = emptyFilter();
    if(S.ds.channels.length)
      log(`関節が ${S.ds.channels.join(' / ')} に分かれています。既定では ${S.signal} だけを描きます`);
    log(`タスク ${S.ds.taskList.length} 種: ${S.ds.taskList.map(t => `${t.task}（${t.n}）`).join(' / ') || 'なし'}`);

    $('#loading').hidden = true;
    $('#viewer').hidden = false;
    showHeader();
    buildControls();
    buildFilterUI();
    renderDeck();
  } catch (err){
    $('#loading').hidden = true;
    $('#drop').hidden = false;
    log(String(err?.message || err), 'err');
    $('#dropError').textContent = String(err?.message || err);
    $('#dropError').hidden = false;
    console.error(err);
  }
}

function reset(){
  S.eng.detachAll();
  clearPosters();
  S.vfs?.revokeAll();
  dropCards();
  S.ds = null; S.vfs = null; S.facets = null; S.missing = new Map();
  S.filter = emptyFilter();
  $('#viewer').hidden = true;
  $('#dropError').hidden = true;
}

/* ---------------- ヘッダ / コントロール ---------------- */

function showHeader(){
  const d = S.ds;
  $('#dsName').textContent = d.name;
  $('#dsChips').innerHTML = [
    ['形式', d.version], ['ロボット', d.robot], ['エピソード', d.episodes.length],
    ['フレーム', d.episodes.reduce((a, e) => a + e.len, 0).toLocaleString()],
    ['録画', `${d.fps} Hz`], ['合計', `${Math.round(d.totalSec / 60)} 分`],
    ['関節', d.joints.length], ['カメラ', d.cams.length],
    ['タスク', `${d.taskList.length} 種`],
  ].map(([k, v]) => `<span class="chip">${k} <b>${esc(v)}</b></span>`).join('');

  // タスクの一覧（多いときは先頭だけ出し、残りは件数でまとめる）
  const shown = d.taskList.slice(0, 3);
  const rest = d.taskList.length - shown.length;
  $('#dsTask').innerHTML = shown.map((t, k) =>
    `<span class="one" title="${esc(t.task)}"><i class="dot" data-tc="${k}"></i>
       <span class="name">${esc(t.task)}</span><span>${t.n}</span></span>`).join('') +
    (rest > 0 ? `<span>他 ${rest} 種</span>` : '');
  paintDots($('#dsTask'));
}

function buildControls(){
  $('#camWrap').hidden = S.view !== 'grid';
  $('#camSel').innerHTML = S.ds.cams.map(c =>
    `<option value="${esc(c)}"${c === S.cam ? ' selected' : ''}>${esc(short(c))}</option>`).join('');
  $('#rowsOpts').hidden = S.view !== 'rows';

  // 系列が分かれているデータセット（OpenArm の pos/vel/torque）でだけ出す
  $('#sigWrap').hidden = !S.ds.channels.length;
  $('#sigSel').innerHTML = [['all', 'すべて'], ...S.ds.channels.map(c => [c, c])]
    .map(([v, t]) => `<option value="${esc(v)}"${v === S.signal ? ' selected' : ''}>${esc(t)}</option>`).join('');

  // 動画の列数はカメラ本数に合わせる（3 固定だと 1 カメラのとき 2/3 が空く）
  $('#dVids').style.setProperty('--vid-cols', String(Math.min(S.ds.cams.length, 3)));
}

const short = c => c.replace(/^observation\.images?\./, '');
const esc = s => String(s).replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));
const taskLabel = t => t === NO_TASK ? '(タスク未設定)' : t;

/* ---------------- フィルタ ---------------- */

/** データセットが変わるたびに、選べる値を作り直す */
function buildFilterUI(){
  const f = S.facets;

  $('#fTaskCount').textContent = `${f.tasks.length} 種`;
  $('#fTasks').innerHTML = f.tasks.map((t, k) =>
    `<button class="taskchip${t.n ? '' : ' zero'}" data-task="${esc(t.task)}" aria-pressed="false"
             title="${esc(taskLabel(t.task))}">
       <i class="dot" data-tc="${t.task === NO_TASK ? -1 : k}"></i>
       <span class="name">${esc(taskLabel(t.task))}</span><span class="n">${t.n}</span>
     </button>`).join('');
  paintDots($('#fTasks'));

  $('#fCam').innerHTML = `<option value="all">すべて</option>` + f.cams.map(c =>
    `<option value="${esc(c.cam)}">${esc(short(c.cam))}（${c.n}）</option>`).join('');
  $('#fFile').innerHTML = `<option value="all">すべて（${f.files.length} 本）</option>` + f.files.map(x =>
    `<option value="${esc(x.path)}">${esc(x.path.split('/').slice(-2).join('/'))}（${x.n}）</option>`).join('');

  // プレースホルダにデータセット全体のレンジを出す（何を入れられるかが分かる）
  $('#fSecMin').placeholder = f.sec[0].toFixed(1);
  $('#fSecMax').placeholder = f.sec[1].toFixed(1);
  $('#fEpMin').placeholder = String(f.ep[0]);
  $('#fEpMax').placeholder = String(f.ep[1]);

  for(const id of ['#fQuery', '#fSecMin', '#fSecMax', '#fEpMin', '#fEpMax']) $(id).value = '';
  for(const id of ['#fCam', '#fVideo', '#fFile']) $(id).value = 'all';
  syncFilterUI();
}

/** 入力欄の中身 → S.filter。空欄と数でないものは「指定なし」 */
const numOrNull = v => v.trim() === '' || !Number.isFinite(+v) ? null : +v;

function readFilterInputs(){
  S.filter.q = $('#fQuery').value;
  S.filter.secMin = numOrNull($('#fSecMin').value);
  S.filter.secMax = numOrNull($('#fSecMax').value);
  S.filter.epMin = numOrNull($('#fEpMin').value);
  S.filter.epMax = numOrNull($('#fEpMax').value);
  S.filter.cam = $('#fCam').value;
  S.filter.video = $('#fVideo').value;
  S.filter.file = $('#fFile').value;
}

/** 選択状態の見た目（チップの押下・要約・解除ボタン）を S.filter に合わせる */
function syncFilterUI(){
  const on = isActive(S.filter);
  for(const b of $$('#fTasks .taskchip'))
    b.setAttribute('aria-pressed', String(S.filter.tasks.has(b.dataset.task)));
  $('#filterPanel').classList.toggle('on', on);
  $('#fClear').hidden = !on;
  const desc = describeFilter(S.filter, S.facets);
  $('#fSummary').textContent = on
    ? `${desc} → ${S.order.length} / ${S.facets.total} 件`
    : `全 ${S.facets.total} エピソード`;
}

function applyFilter(){
  readFilterInputs();
  renderDeck();
}

/* ---------------- デッキ描画（グリッド / 行） ---------------- */

function sortedOrder(){
  return orderOf(S.ds, S.filter, S.sort, { missing: S.missing });
}

/**
 * 現在のカードを捨てる。visible を落としてから捨てるのが要点で、
 * これで「await から戻ってきた割り当て / ポスター取得」が自分は用済みだと気付ける。
 * 落とさないと、消えたはずの DOM に <video> が生えて上限の枠だけを食う。
 */
function dropCards(){
  for(const c of S.cards.values()) c.visible = false;
  S.cards.clear();
  S.attaching.clear();
}

function renderDeck(){
  S.eng.detachAll();
  dropCards();
  S.order = sortedOrder();
  const deck = $('#deck');
  deck.dataset.view = S.view;
  const camsFor = S.view === 'grid' ? [S.cam] : S.ds.cams;
  deck.style.setProperty('--stage-cols', String(Math.min(camsFor.length, 3)));

  // タスクが 1 種類しか無いデータセットでは、全カードに同じ文字列が並ぶだけなので出さない
  const showTask = S.ds.taskList.length > 1;

  deck.innerHTML = S.order.map(i => {
    const e = S.ds.episodes[i];
    const stages = camsFor.map(c =>
      `<span class="stage" data-cam="${esc(c)}"><span class="tag">${esc(short(c))}</span></span>`).join('');
    const label = e.tasks.join(' / ');
    const tc = e.tasks.length ? S.ds.taskRank(e.tasks[0]) : -1;
    const task = showTask
      ? `<span class="eptask"><i class="dot" data-tc="${tc}"></i><span class="name">${esc(taskLabel(label))}</span></span>`
      : '';
    const title = label ? ` title="${esc(label)}"` : '';
    if(S.view === 'grid') return `<article class="card tile" data-i="${i}"${title}>
        <span class="stages">${stages}</span>
        <span class="prog"><i></i></span>
        <span class="cap"><b>#${i}</b><span>${e.sec.toFixed(1)}s · ${e.len}f</span></span>
        ${task}
      </article>`;
    return `<article class="card row" data-i="${i}"${title}>
        <div class="rowhead"><b>#${i}</b><span>${e.sec.toFixed(1)} s</span><span>${e.len} frames</span>
          ${task}
          <span class="t" data-t>0.0 s</span>
          <button class="mini" data-act="open">▶ 詳細</button></div>
        <div class="stages">${stages}</div>
        <span class="prog"><i></i></span>
        <svg class="spark" viewBox="0 0 600 64" preserveAspectRatio="none"></svg>
      </article>`;
  }).join('');
  paintDots(deck);

  for(const el of $$('#deck .card')){
    const i = +el.dataset.i;
    S.cards.set(i, {
      i, el,
      stages: [...el.querySelectorAll('.stage')].map(s => ({ el: s, cam: s.dataset.cam })),
      bar: el.querySelector('.prog i'),
      spark: el.querySelector('.spark'),
      tlab: el.querySelector('[data-t]'),
      visible: false, posters: false, sparked: false,
    });
  }
  io.disconnect();
  for(const c of S.cards.values()) io.observe(c.el);

  const all = S.ds.episodes.length;
  $('#count').textContent = S.order.length === all
    ? `${all} エピソード`
    : `${S.order.length} / ${all} エピソード`;
  $('#empty').hidden = S.order.length > 0;
  syncFilterUI();
}

const io = new IntersectionObserver(entries => {
  for(const en of entries){
    const c = S.cards.get(+en.target.dataset.i);
    if(!c) continue;
    c.visible = en.isIntersecting;
    if(en.isIntersecting){ loadPosters(c); loadSpark(c); }
  }
  refresh();
}, { rootMargin: '300px' });

async function loadPosters(c){
  if(c.posters) return;
  c.posters = true;
  const e = S.ds.episodes[c.i];
  let skipped = false;
  for(const st of c.stages){
    const url = await S.ds.videoUrl(c.i, st.cam).catch(() => null);
    if(!url){ st.el.classList.add('missing'); continue; }
    const cm = e.cams[st.cam];
    // 画面外へ出たカードの分は捨てる。長くスクロールすると溜まった順番待ちが
    // デコーダを占め続け、いま見ているカードのポスターと再生が後回しになる。
    const p = await poster(url, cm.a + (cm.b - cm.a) * 0.35, 256, () => c.visible);
    if(p === SKIPPED) skipped = true;
    else if(p) st.el.style.backgroundImage = `url(${p})`;
  }
  if(!skipped) return;
  // skip は画面外に出たときにしか起きない。ここで visible を条件にすると
  // 常に false のままになり、戻ってきても二度と取りに行かなくなる。
  c.posters = false;
  // 印を戻すのは最後なので、それまでに来た IntersectionObserver / kickPosters は
  // 「読み込み中」と見て素通りしている。見えたまま終わったなら自分で掛け直す。
  if(c.visible) kickPosters();
}

async function loadSpark(c){
  if(c.sparked || !c.spark) return;
  c.sparked = true;
  try {
    const traj = await S.ds.trajectory(c.i);
    if(!S.ranges) S.ranges = S.ds.range || rangeOf(traj);
    c.spark.innerHTML = sparkline(traj, S.ranges, { show: shownJoints(), colors: S.jointColors });
  } catch (err){ console.warn('軌道の読み込みに失敗', c.i, err); }
}

/** カードから <video> を外す（detach の取りこぼしも掃除する） */
function dropVideo(key){
  S.eng.detach(key);
  S.cards.get(key)?.stages.forEach(s => s.el.querySelectorAll('video').forEach(v => v.remove()));
}

/**
 * 画面内のカードを、画面中央に近い順に上限まで返す。
 * 並び順（= 文書順）で先に埋めると、下へスクロールしたとき枠を使い切るのは
 * 画面の上端より上（IntersectionObserver の余白 300px の中）のカードで、
 * いま見ているカードが最後まで再生されないままになる。
 */
function nearestCards(){
  const mid = innerHeight / 2, near = [];
  for(const i of S.order){
    const c = S.cards.get(i);
    if(!c?.visible || c.dead) continue;
    const r = c.el.getBoundingClientRect();
    near.push([Math.abs(r.top + r.height / 2 - mid), c]);
  }
  near.sort((a, b) => a[0] - b[0]);
  return near.slice(0, S.eng.cap).map(([, c]) => c);
}

/**
 * 表示中のカードに <video> を割り当て直す。
 * 上限は active だけでなく「割り当て中」も数える。attachCard は await を挟むので、
 * 数え漏らすとスクロール中の連続呼び出しで上限の数倍が同時に生き、
 * デコーダが詰まってどのカードも再生されなくなる。
 */
function refresh(){
  for(const key of [...S.eng.active.keys()]){
    const c = S.cards.get(key);
    if(!c || !c.visible) dropVideo(key);              // 画面外に出たものは必ず外す
  }
  const want = nearestCards();
  // スクロール中に付け替えると、どれも読み込みが終わらないまま入れ替わる。
  // 空いた枠を埋めるだけにして、選び直しは止まってから（onScroll）行う。
  if(!S.scrolling){
    const keep = new Set(want.map(c => c.i));
    for(const key of [...S.eng.active.keys()]) if(!keep.has(key)) dropVideo(key);
  }
  for(const c of want){
    if(S.eng.active.size + S.attaching.size >= S.eng.cap) break;
    if(S.eng.active.has(c.i) || S.attaching.has(c)) continue;
    attachCard(c);
  }
}

async function attachCard(c){
  S.attaching.add(c);
  try {
    const e = S.ds.episodes[c.i];
    const slots = [];
    for(const st of c.stages){
      const url = await S.ds.videoUrl(c.i, st.cam);
      if(!url){ c.dead = true; return; }               // 動画が無いカードを毎回試さない
      slots.push({ stage: st.el, url, a: e.cams[st.cam].a, b: e.cams[st.cam].b });
    }
    // await 中に画面外へ出た / 描き直された場合は捨てる
    if(!c.visible || S.eng.active.has(c.i)) return;
    S.eng.attach(c.i, slots);
  } catch (err){
    // 読めない動画（展開に失敗した等）。dead にしないと、下の配り直しで
    // 同じカードを永久に試し続ける。vfs は失敗した promise も覚えているので即座に回る
    c.dead = true;
    console.warn('動画を用意できませんでした', c.i, err);
  } finally {
    S.attaching.delete(c);
    // 終わったら必ず配り直す。取り止めた枠（動画が無い / 画面外に出た）は
    // 空いたままになるし、用意に手間取ったカードは、スクロールが止まったあとの
    // 選び直しに間に合わず「中央でないカードが枠を持ったまま」になる。
    scheduleRefresh();
  }
}

/** 空いた枠の配り直しをまとめる。同じ間に何度呼ばれても 1 回にする */
let refreshTimer = 0;
function scheduleRefresh(){
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refresh, 0);
}

/**
 * スクロールが止まったら選び直す。IntersectionObserver は「またいだ瞬間」しか鳴らないので、
 * 惰性で止まった位置に対してはここが最後の一押しになる。
 */
let scrollTimer = 0;
function onScroll(){
  S.scrolling = true;
  clearTimeout(scrollTimer);
  scrollTimer = setTimeout(() => { S.scrolling = false; refresh(); kickPosters(); }, 140);
}

/**
 * 画面外へ出て捨てたポスターを取り直す。IntersectionObserver は「入った瞬間」しか
 * 鳴らないので、捨てた印が付くのがそれより後だと、出たり入ったりするまで取りに行かない。
 * 止まったところと、捨てた印を戻したところで拾えば、見ているカードが黒いままにならない。
 */
let posterTimer = 0;
function kickPosters(){
  clearTimeout(posterTimer);
  posterTimer = setTimeout(() => {
    for(const c of S.cards.values()) if(c.visible) loadPosters(c);
  }, 0);
}

function tick(now){
  requestAnimationFrame(tick);
  S.eng.poke(now);
  for(const key of S.eng.active.keys()){
    const f = S.eng.step(key);
    if(f === null) continue;
    const c = S.cards.get(key);
    if(!c) continue;
    c.bar.style.width = `${(f * 100).toFixed(1)}%`;
    if(c.tlab) c.tlab.textContent = `${(f * S.ds.episodes[key].sec).toFixed(1)} s`;
    const ph = c.spark?.querySelector('.ph');
    if(ph){ const x = f * 600; ph.setAttribute('x1', x); ph.setAttribute('x2', x); }
  }
}

/* ---------------- 再生の操作（ボタンとショートカットで共有） ---------------- */

function setPlaying(on){
  S.eng.setRunning(on);
  $('#pause').textContent = on ? '⏸ 一時停止' : '▶ 再生';
}

/** デッキ全体を先頭（0:00:00）から再生し直す */
function restartAll(){
  S.eng.restart();
  for(const c of S.cards.values()){
    c.bar.style.width = '0%';                          // まだ枠が回ってこないカードも見た目を揃える
    if(c.tlab) c.tlab.textContent = '0.0 s';
  }
  setPlaying(true);
}

/* ---------------- 詳細ダイアログ ---------------- */

const D = { i: -1, vids: {}, raf: 0, playing: true, hidden: new Set(), traj: null };

async function openDetail(i){
  const d = S.ds, e = d.episodes[i];
  D.i = i;
  $('#dTitle').textContent = `エピソード #${i}`;
  const missing = S.missing.get(i) ?? [];
  $('#dChips').innerHTML = [['長さ', `${e.sec.toFixed(1)} s`], ['フレーム', e.len],
    ['レート', `${d.fps} Hz`], ['タスク', taskLabel(e.tasks.join(' / '))],
    ...(missing.length ? [['動画なし', missing.map(short).join(', ')]] : [])]
    .map(([k, v]) => `<span class="chip">${k} <b>${esc(v)}</b></span>`).join('');

  if(!$('#dVids').childElementCount){
    $('#dVids').innerHTML = d.cams.map(c =>
      `<div class="vwrap"><span class="tag">${esc(short(c))}</span>
       <video data-cam="${esc(c)}" muted playsinline preload="metadata"></video></div>`).join('');
    for(const c of d.cams) D.vids[c] = $(`#dVids video[data-cam="${CSS.escape(c)}"]`);
  }
  buildLegend();

  for(const c of d.cams){
    const v = D.vids[c], url = await d.videoUrl(i, c);
    if(!url) continue;
    if(v.src !== url) v.src = url;
    const go = () => { v.playbackRate = 1; v.currentTime = e.cams[c].a; v.play().catch(() => {}); };
    v.readyState >= 1 ? go() : v.addEventListener('loadedmetadata', go, { once: true });
  }
  D.playing = true; $('#dPP').textContent = '⏸';
  if(!$('#dlg').open) $('#dlg').showModal();

  D.traj = await S.ds.trajectory(i).catch(() => null);
  if(D.traj){
    if(!S.ranges) S.ranges = rangeOf(D.traj);
    drawDetailChart();
  }
  cancelAnimationFrame(D.raf); D.raf = requestAnimationFrame(detailLoop);
}

/** 凡例は「いま描いている関節」だけを出す（24 本あるデータセットで全部並べても選べない） */
function buildLegend(){
  const show = shownJoints();
  $('#dLegend').innerHTML = S.ds.joints.map((j, k) => ({ j, k }))
    .filter(({ k }) => !show || show.has(k))
    .map(({ j, k }) =>
      `<button data-j="${k}" aria-pressed="${!D.hidden.has(k)}"><span class="sw" data-jc="${k}"></span>${esc(j)}</button>`)
    .join('');
  // style 属性は CSP (style-src 'self') に弾かれるので、色は CSSOM で塗る
  for(const sw of $$('#dLegend .sw')) sw.style.background = S.jointColors[+sw.dataset.jc];
}

function drawDetailChart(){
  if(D.traj) $('#dChart').innerHTML =
    chart(D.traj, S.ranges, { hidden: D.hidden, show: shownJoints(), colors: S.jointColors });
}

function detailLoop(){
  D.raf = requestAnimationFrame(detailLoop);
  const d = S.ds, e = d.episodes[D.i];
  if(!e) return;
  const c0 = d.cams[0], m = e.cams[c0], v0 = D.vids[c0];
  if(!v0 || v0.readyState < 1) return;
  let f = (v0.currentTime - m.a) / (m.b - m.a || 1);
  if(f >= 1 || f < -0.05){ d.cams.forEach(c => D.vids[c] && (D.vids[c].currentTime = e.cams[c].a)); f = 0; }
  else for(const c of d.cams.slice(1)){
    const v = D.vids[c]; if(!v || v.readyState < 1 || v.seeking) continue;
    const tgt = e.cams[c].a + f * (e.cams[c].b - e.cams[c].a);
    if(Math.abs(v.currentTime - tgt) > 0.2) v.currentTime = tgt;
  }
  f = Math.max(0, Math.min(1, f));
  $('#dSeek').value = Math.round(f * 1000);
  $('#dTime').textContent = `${(f * e.sec).toFixed(1)} / ${e.sec.toFixed(1)} s · frame ${Math.round(f * (e.len - 1))}/${e.len - 1}`;
  const ph = $('#dChart').querySelector('.ph');
  if(ph){ ph.setAttribute('x1', f * 1000); ph.setAttribute('x2', f * 1000); }
}

function closeDetail(){
  cancelAnimationFrame(D.raf);
  for(const v of Object.values(D.vids)) v.pause();
}
function stepDetail(dir){
  const k = S.order.indexOf(D.i);
  openDetail(S.order[(k + dir + S.order.length) % S.order.length]);
}
/** 詳細ダイアログの動画を先頭（0:00:00）から再生し直す */
function restartDetail(){
  const e = S.ds?.episodes[D.i];
  if(!e) return;
  for(const c of S.ds.cams){ const v = D.vids[c]; if(v) v.currentTime = e.cams[c].a; }
  if(!D.playing) toggleDetailPlay();
}
function toggleDetailPlay(){
  D.playing = !D.playing;
  $('#dPP').textContent = D.playing ? '⏸' : '▶';
  for(const v of Object.values(D.vids)) D.playing ? v.play().catch(() => {}) : v.pause();
}

/* ---------------- 配線 ---------------- */

export function boot(){
  // body は height:100% なので、内容がはみ出すと body の外（html 領域）に落ちうる。
  // そこで取りこぼすとブラウザがファイルを開いてページごと消えるため、window で受ける。
  const dz = window, mark = on => document.body.classList.toggle('dragging', on);
  let depth = 0;
  dz.addEventListener('dragenter', e => { e.preventDefault(); if(++depth === 1) mark(true); });
  dz.addEventListener('dragover', e => { e.preventDefault(); if(e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; });
  dz.addEventListener('dragleave', e => { e.preventDefault(); if(--depth <= 0){ depth = 0; mark(false); } });
  dz.addEventListener('drop', async e => {
    e.preventDefault(); depth = 0; mark(false);
    const inputs = await filesFromDataTransfer(e.dataTransfer);
    if(!inputs.length) return dropFailed('ドロップされた項目からファイルを読み取れませんでした。フォルダかアーカイブを渡してください。');
    ingest(inputs);
  });

  $('#pickFiles').onclick = () => $('#fileInput').click();
  $('#pickDir').onclick = () => $('#dirInput').click();
  for(const id of ['#fileInput', '#dirInput'])
    $(id).onchange = e => {
      const inputs = [...e.target.files].map(f => ({ file: f, path: f.webkitRelativePath || f.name }));
      e.target.value = '';                                  // 同じフォルダを選び直せるようにする
      if(!inputs.length) return dropFailed('ファイルが選択されませんでした。');
      ingest(inputs);
    };

  $('#again').onclick = () => { reset(); $('#drop').hidden = false; };
  $('#clearCache').onclick = async () => { await clearStaging(); log('OPFS の一時展開データを削除しました', 'ok'); };

  $$('#viewTabs button').forEach(b => b.onclick = () => {
    S.view = b.dataset.view;
    $$('#viewTabs button').forEach(x => x.setAttribute('aria-pressed', x === b));
    S.eng.setCap(S.view === 'grid' ? +$('#capSel').value : Math.max(2, Math.round(+$('#capSel').value / 3)));
    buildControls();
    renderDeck();
  });

  $('#camSel').onchange = e => { S.cam = e.target.value; renderDeck(); };
  $('#rateSel').onchange = e => {
    S.rate = +e.target.value;
    S.eng.setRate(S.rate);
    $('#hz').textContent = `${(S.ds.fps * S.rate).toFixed(1)} Hz`;
  };
  $('#capSel').onchange = e => {
    S.eng.setCap(S.view === 'grid' ? +e.target.value : Math.max(2, Math.round(+e.target.value / 3)));
    refresh();
  };
  $('#sortSel').onchange = e => { S.sort = e.target.value; renderDeck(); };
  $('#sigSel').onchange = e => {
    S.signal = e.target.value;
    renderDeck();                                   // スパークラインを引き直す
    if($('#dlg').open){ buildLegend(); drawDetailChart(); }
  };

  // フィルタ。検索欄は打つたびにデッキを組み直すと重いので少し待つ
  let qTimer = 0;
  $('#fQuery').oninput = () => { clearTimeout(qTimer); qTimer = setTimeout(applyFilter, 200); };
  $('#fQuery').onchange = () => { clearTimeout(qTimer); applyFilter(); };
  for(const id of ['#fSecMin', '#fSecMax', '#fEpMin', '#fEpMax']) $(id).oninput = applyFilter;
  for(const id of ['#fCam', '#fVideo', '#fFile']) $(id).onchange = applyFilter;
  $('#fTasks').addEventListener('click', e => {
    const b = e.target.closest('.taskchip'); if(!b) return;
    const t = b.dataset.task;
    S.filter.tasks.has(t) ? S.filter.tasks.delete(t) : S.filter.tasks.add(t);
    applyFilter();
  });
  $('#fClear').onclick = () => {
    S.filter = emptyFilter();
    for(const id of ['#fQuery', '#fSecMin', '#fSecMax', '#fEpMin', '#fEpMax']) $(id).value = '';
    for(const id of ['#fCam', '#fVideo', '#fFile']) $(id).value = 'all';
    renderDeck();
  };
  $('#pause').onclick = () => setPlaying(!S.eng.running);
  $('#restart').onclick = restartAll;
  $('#colSeg').onclick = e => {
    const b = e.target.closest('button'); if(!b) return;
    [...$('#colSeg').children].forEach(x => x.setAttribute('aria-pressed', x === b));
    b.dataset.c === 'auto' ? $('#deck').removeAttribute('data-cols') : ($('#deck').dataset.cols = b.dataset.c);
  };
  $('#minW').oninput = e => {
    document.documentElement.style.setProperty('--card-min', `${e.target.value}px`);
    $('#minWv').textContent = `${e.target.value}px`;
  };

  $('#deck').addEventListener('click', e => {
    const card = e.target.closest('.card'); if(!card) return;
    if(e.target.closest('.stages') || e.target.closest('[data-act="open"]')) openDetail(+card.dataset.i);
  });
  $('#deck').addEventListener('mousemove', e => {
    const stages = e.target.closest('.stages'); if(!stages) return;
    const card = stages.closest('.card'), i = +card.dataset.i;
    if(!S.eng.active.has(i)) return;
    const r = stages.getBoundingClientRect();
    S.eng.hold(i, true);
    S.eng.scrub(i, Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
  });
  $('#deck').addEventListener('mouseout', e => {
    const stages = e.target.closest('.stages');
    if(stages && !stages.contains(e.relatedTarget)) S.eng.hold(+stages.closest('.card').dataset.i, false);
  });

  $('#dClose').onclick = () => $('#dlg').close();
  $('#dPrev').onclick = () => stepDetail(-1);
  $('#dNext').onclick = () => stepDetail(1);
  $('#dPP').onclick = toggleDetailPlay;
  $('#dRestart').onclick = restartDetail;
  $('#dSeek').oninput = e => {
    const e0 = S.ds.episodes[D.i], f = e.target.value / 1000;
    for(const c of S.ds.cams){ const v = D.vids[c]; if(v) v.currentTime = e0.cams[c].a + f * (e0.cams[c].b - e0.cams[c].a); }
  };
  $('#dLegend').addEventListener('click', e => {
    const b = e.target.closest('button'); if(!b) return;
    const j = +b.dataset.j;
    D.hidden.has(j) ? D.hidden.delete(j) : D.hidden.add(j);
    b.setAttribute('aria-pressed', !D.hidden.has(j));
    drawDetailChart();
  });
  $('#dlg').addEventListener('close', closeDetail);
  /** 入力欄では奪わない（検索語に空白や数字を打てなくなる） */
  const isField = el => !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
  /** ボタンや summary の Space は本来の操作（押す・開く）なので、そちらに譲る */
  const isActivatable = el => !!el && /^(BUTTON|SUMMARY|A)$/.test(el.tagName);

  addEventListener('keydown', e => {
    if(e.metaKey || e.ctrlKey || e.altKey || isField(e.target)) return;
    const dlg = $('#dlg').open;
    if(!dlg && $('#viewer').hidden) return;             // まだデータセットが無い
    if(e.key === ' ' && !isActivatable(e.target)){
      e.preventDefault();
      dlg ? toggleDetailPlay() : setPlaying(!S.eng.running);
    } else if(e.key === 'Home' || e.key === '0'){
      e.preventDefault();
      dlg ? restartDetail() : restartAll();
    } else if(dlg && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')){
      e.preventDefault();
      stepDetail(e.key === 'ArrowRight' ? 1 : -1);
    }
  });

  addEventListener('scroll', onScroll, { passive: true });

  requestAnimationFrame(tick);

  // E2E / デバッグ用のフック（ページ内からのみ使用。外部通信はしない）
  window.LRV = {
    ingest,
    state: S,
    /** 圧縮コーデックを解決する（WASM が使える環境かの確認用） */
    compressors: getCompressors,
    /** URL から取得したアーカイブを、ドロップされたものとして流し込む */
    async ingestUrl(url, name = url.split('/').pop()){
      const blob = await (await fetch(url)).blob();
      return ingest([{ file: new File([blob], name), path: name }]);
    },
  };
}
